import { FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { StoredTrackingParams, UTMParams, TrackingParams } from './tracking.types';
import Clarity from '@microsoft/clarity';
import posthog from 'posthog-js';
import {
  getClarityProjectId,
  getGaMeasurementId,
  getPostHogKey,
  initMixpanel,
  META_PIXEL_ID,
  POSTHOG_HOST,
  USER_TYPE_TENANT,
} from './analytics';
import {
  AddPropertyFormValues,
  AddPropertyManager,
  EditCardProps,
  editPropertyProps,
  formProps,
  PersonalInfoFormValues,
  RentalInfoFormProps,
  AddCardFormValues,
  RentAddressFormValues,
  RentAddressOccupationFormValues,
  ManualAddressValues,
  UpdatePaymentOptionValues,
  AddEquifaxBuilding,
  RNTFormValues,
  FullRNTFormValues,
} from '../DatePicker/DatePicker';

dayjs.extend(utc);
dayjs.extend(timezone);

const MONTH_NAMES_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const MONTH_NAMES_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const MONTH_NAMES_MIXED = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'June',
  'July',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
] as const;

export const standardizeDate = (date: string) => {
  const dateParts = date.split(' ');
  if (dateParts.length !== 3) {
    throw new Error('Invalid date format. Expected format: DD MMM YYYY');
  }
  const monthMap = {
    jan: 1,
    feb: 2,
    mar: 3,
    apr: 4,
    may: 5,
    jun: 6,
    jul: 7,
    aug: 8,
    sep: 9,
    oct: 10,
    nov: 11,
    dec: 12,
  };

  const monthLower = dateParts[1].toLowerCase();
  if (!Object.prototype.hasOwnProperty.call(monthMap, monthLower)) {
    throw new Error('Invalid month name');
  }
  const month = monthMap[monthLower as keyof typeof monthMap];
  const standardizedDate = new Date(Number(dateParts[2]), month - 1, Number(dateParts[0]));
  if (isNaN(standardizedDate.getTime())) {
    throw new Error('Invalid date');
  }
  const year = standardizedDate.getFullYear();
  const formattedMonth = String(standardizedDate.getMonth() + 1).padStart(2, '0');
  const formattedDay = String(standardizedDate.getDate()).padStart(2, '0');
  return `${year}-${formattedMonth}-${formattedDay}`;
};

export const convertToUTC = (date: string | Date): string => {
  return dayjs(date).utc().toISOString();
};

export const convertToLocalReadable = (date: string | Date): string => {
  return dayjs(date).local().format('MMM DD, YYYY');
};

// d MM YYYY
export const formatDate = (date: Date | null) => {
  return `${('0' + date?.getDate()).slice(-2)} ${
    date ? MONTH_NAMES_SHORT[date.getMonth()] : 0
  } ${date?.getFullYear()}`;
};
export const formatDateToDisplay = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
  let formattedDate = date.toLocaleDateString('en-GB', options);
  formattedDate = formattedDate.replace('Sept', 'Sep');
  return formattedDate;
};
export const formatDateToDisplayUTC = (dateString: string): string => {
  const date = new Date(dateString);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const monthIndex = date.getUTCMonth();
  const year = date.getUTCFullYear();

  const month = MONTH_NAMES_SHORT[monthIndex];
  return `${day} ${month} ${year}`;
};
export const formatDateToDisplayUTCLong = (dateString: string): string => {
  const date = new Date(dateString);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const monthIndex = date.getUTCMonth();
  const year = date.getUTCFullYear();

  const month = MONTH_NAMES_LONG[monthIndex];
  return `${day} ${month} ${year}`;
};
export const formatDateToDisplayUTCMonth = (dateString: string): string => {
  const date = new Date(dateString);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const monthIndex = date.getUTCMonth();
  const year = date.getUTCFullYear();

  const month = MONTH_NAMES_MIXED[monthIndex];
  return `${day} ${month}, ${year}`;
};
export const formatDateToDisplayUTCShort = (dateString: string): string => {
  const date = new Date(dateString); // Parse date string as UTC
  const day = String(date.getUTCDate()).padStart(2, '0');
  const monthIndex = date.getUTCMonth();

  const month = MONTH_NAMES_SHORT[monthIndex];

  return `${day} ${month}`; // Day and short month only
};

export const parseDateStringToDate = (dateString: string): Date | null => {
  const [day, month, year] = dateString.split(' ');
  const date = new Date(`${day} ${month} ${year}`);
  return isNaN(date.getTime()) ? null : date;
};
export const formatDateToDisplayLong = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'long', year: 'numeric' };
  return date.toLocaleDateString('en-GB', options);
};
export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
export const formatDateToDisplayShortMonth = (dateString: string): string => {
  if (!dateString) return '';
  const trimmed = String(dateString).trim();
  const ymdMatch = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z)?$/.exec(trimmed);
  if (ymdMatch) {
    const [, year, month, day] = ymdMatch;
    const monthName = MONTH_NAMES_SHORT[Number(month) - 1] || '';
    return `${monthName} ${Number(day)}, ${year}`;
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  const monthName = MONTH_NAMES_SHORT[date.getMonth()] || '';
  const day = date.getDate();
  const year = date.getFullYear();
  return `${monthName} ${day}, ${year}`;
};

export const formatDateToDisplayShortLocalMonth = (dateString: string): string => {
  if (!dateString) return '';

  const trimmed = String(dateString).trim();
  const ymdMatch = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z)?$/.exec(trimmed);
  if (ymdMatch) {
    const [, yearStr, monthStr, dayStr] = ymdMatch;
    const year = parseInt(yearStr, 10);
    const monthIndex = parseInt(monthStr, 10) - 1;
    const day = parseInt(dayStr, 10);

    const month = MONTH_NAMES_SHORT[monthIndex];
    return month ? `${month} ${day}, ${year}` : '';
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  const day = date.getDate(); // Local day (no zero padding)
  const monthIndex = date.getMonth(); // Local month
  const year = date.getFullYear(); // Local year

  const month = MONTH_NAMES_SHORT[monthIndex];
  return month ? `${month} ${day}, ${year}` : '';
};

export const formatDueDate = (day: number, isPaid?: boolean) => {
  const date = new Date();
  if (isPaid) {
    // If the payment is made, set the date to the next month
    date.setMonth(date.getMonth() + 1);
  }
  date.setDate(day);
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'long', year: 'numeric' };
  return date.toLocaleDateString('en-GB', options);
};
export const convertToRgb = (color: string) => {
  const tempElement = document.createElement('div');
  tempElement.style.color = color;
  document.body.appendChild(tempElement);
  // Get the computed color in RGB format
  const computedColor = getComputedStyle(tempElement).color;
  document.body.removeChild(tempElement);

  // Extract the RGB values from the computed color
  const rgbValues = computedColor.match(/\d+/g)?.map(Number);

  if (rgbValues && rgbValues.length >= 3) {
    return { r: rgbValues[0], g: rgbValues[1], b: rgbValues[2] };
  }

  // Default to black if the color conversion fails
  return { r: 0, g: 0, b: 0 };
};
interface AddressFetchProps {
  addressInputRef: React.MutableRefObject<HTMLInputElement | null>;
  isSaveManually: (value: boolean) => void;
  setIsAutocompleteOpen?: (value: boolean) => void;
  formik:
    | FormikProps<AddPropertyFormValues>
    | FormikProps<PersonalInfoFormValues>
    | FormikProps<RentalInfoFormProps>
    | FormikProps<formProps>
    | FormikProps<editPropertyProps>
    | FormikProps<AddPropertyManager>
    | FormikProps<EditCardProps>
    | FormikProps<AddCardFormValues>
    | FormikProps<RentAddressFormValues>
    | FormikProps<RentAddressOccupationFormValues>
    | FormikProps<ManualAddressValues>
    | FormikProps<UpdatePaymentOptionValues>
    | FormikProps<AddEquifaxBuilding>
    | FormikProps<FullRNTFormValues>
    | FormikProps<RNTFormValues>;
}
export const FetchAddress: React.FC<AddressFetchProps> = ({
  addressInputRef,
  formik,
  isSaveManually,
  setIsAutocompleteOpen,
}) => {
  const [changed, setChanged] = useState(false);
  useEffect(() => {
    if (window.google && addressInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        types: ['address'],
        componentRestrictions: { country: ['CA'] },
      });
      const handlePlaceChanged = () => {
        const place = autocomplete.getPlace();
        if (setIsAutocompleteOpen) setIsAutocompleteOpen(false);
        if (place && place.address_components) {
          let house = '';
          let street = '';
          let city = '';
          let province = '';
          let provinceCode = '';
          let postal = '';
          place.address_components.forEach((component: AddressComponent) => {
            const types = component.types;
            if (types.includes('street_number')) {
              house = component.long_name;
            }
            if (types.includes('route')) {
              street = component.long_name;
            }
            if (types.includes('locality')) {
              city = component.long_name;
            } else if (
              !city &&
              (types.includes('sublocality_level_1') ||
                types.includes('sublocality') ||
                types.includes('postal_town') ||
                types.includes('administrative_area_level_3'))
            ) {
              city = component.long_name;
            }
            if (types.includes('administrative_area_level_1')) {
              province = component.long_name;
              provinceCode = component.short_name;
            }
            if (types.includes('postal_code')) {
              postal = component.long_name;
            }
          });
          formik.setFieldValue('address', place.formatted_address);
          formik.setFieldValue('house', house);
          formik.setFieldValue('streetNumber', house);
          formik.setFieldValue('street', street);
          formik.setFieldValue('city', city);
          formik.setFieldValue('province', province);
          formik.setFieldValue('provinceCode', provinceCode);
          formik.setFieldValue('postal', postal);
          formik.setFieldValue('zip', postal);

          // Street number is optional and often omitted by Google Places.
          // Only expand the manual fields when required details are missing.
          if (!city || !province || !postal) {
            isSaveManually(true);
          }
        }
        setChanged(!changed);
      };
      const handleFocus = () => {
        if (setIsAutocompleteOpen) setIsAutocompleteOpen(true);
      };

      const handleBlur = () => {
        setTimeout(() => {
          if (setIsAutocompleteOpen) setIsAutocompleteOpen(false);
        }, 200);
      };
      autocomplete.addListener('place_changed', handlePlaceChanged);
      addressInputRef.current.addEventListener('focus', handleFocus);
      addressInputRef.current.addEventListener('blur', handleBlur);
      formik.validateForm();
      return () => {
        addressInputRef.current?.removeEventListener('focus', handleFocus);
        addressInputRef.current?.removeEventListener('blur', handleBlur);
      };
    }
  }, [changed]);
  return null;
};
export const formatPhoneNumber = (value: string, isExtension?: boolean) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return isExtension
    ? `+1- (${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
    : `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};
export const lightenColor = (hex: string, percent: number) => {
  let hexCode = hex.replace('#', '');
  if (hexCode.length === 3) {
    // Convert shorthand (e.g., #abc) to full form (e.g., #aabbcc)
    hexCode = hexCode
      .split('')
      .map((c) => c + c)
      .join('');
  }
  // Parse the hex code into its RGB components
  let r = parseInt(hexCode.substring(0, 2), 16);
  let g = parseInt(hexCode.substring(2, 4), 16);
  let b = parseInt(hexCode.substring(4, 6), 16);

  // Calculate the new RGB values after lightening
  r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
  g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
  b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

  // Convert the RGB values back to hex format
  const toHex = (value: number) => {
    const hex = value.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};
export const getCssVariable = (variableName: string) => {
  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
};
export const questions = [
  'What is the name of your first pet?',
  'What was the make and model of your first car?',
  'In what city were you born?',
  "What is your mother's maiden name?",
  'What was the name of your first school?',
  'What is the name of the street you grew up on?',
  'What is your favorite childhood memory?',
  "What is your favorite teacher's name?",
  'What was the name of your first employer?',
  'What was the name of your best friend in high school?',
  'What was the first concert you attended?',
  'What is the name of your favorite book?',
  'What is your favorite sports team?',
  'What is the middle name of your oldest sibling?',
  'What was the name of your childhood best friend?',
];

export const loadGoogleAnalytics = () => {
  if (typeof window === 'undefined') return;

  if (document.getElementById('ga-script')) return;

  // Inline stub so early track/set calls queue before the remote script loads.
  // Must push the native `arguments` object — gtag.js ignores plain Arrays from rest params.
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  const measurementId = getGaMeasurementId();

  window.gtag('js', new Date());
  // The app sends page_view itself (usePageAnalytics) once auth has resolved, so the
  // user_id/user_type are already set; the automatic one would go out without them.
  window.gtag('config', measurementId, { send_page_view: false });

  const script = document.createElement('script');
  script.id = 'ga-script';
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  document.head.appendChild(script);
};

export const loadClarity = (): void => {
  if (typeof window === 'undefined') return;

  // Package injects script with id "clarity-script"; guard matches loadGoogleAnalytics.
  if (document.getElementById('clarity-script')) return;

  Clarity.init(getClarityProjectId());
  Clarity.setTag('user_type', USER_TYPE_TENANT);
};

export const loadPostHog = (): void => {
  if (typeof window === 'undefined') return;

  // Guard against double-init (posthog-js sets __loaded after init).
  if (posthog.__loaded) return;

  posthog.init(getPostHogKey(), {
    api_host: POSTHOG_HOST,
    autocapture: false,
    capture_pageview: false,
    // Recording is started per route by syncSessionRecording, never globally.
    disable_session_recording: true,
    session_recording: {
      maskTextSelector: '*',
      blockSelector: '[data-ph-no-capture], .ph-no-capture',
    },
  });
};

export const loadMixpanel = (): void => {
  initMixpanel();
};

export const loadMetaPixel = (): void => {
  if (typeof window === 'undefined') return;

  if (document.getElementById('meta-pixel-script')) return;

  // Standard Meta Pixel base-code stub so early fbq() calls queue before fbevents.js loads.
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as FbqFunction;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  window._fbq = window._fbq || fbq;

  const script = document.createElement('script');
  script.id = 'meta-pixel-script';
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  // Consent is granted per route by trackMetaPixelPageView; start revoked so the pixel
  // sends nothing from non-acquisition pages.
  window.fbq('consent', 'revoke');
  window.fbq('init', META_PIXEL_ID);
};

// utils/loadScript.ts
export function loadExternalScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve(); // already loaded
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Script load error for ${src}`));
    document.body.appendChild(script);
  });
}
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const TRACKING_KEYS = [
  'gclid',
  'fbclid',
  'msclkid',
  'ttclid',
  'liFatId',
  'twclid',
  'gbraid',
  'wbraid',
] as const;
const REFERRER_KEYS = ['utm_id', 'referrer_url', 'referrer_domain'] as const;

const getCookie = (name: string): string | null => {
  const match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return match ? decodeURIComponent(match.pop()!) : null;
};

const setCookie = (name: string, value: string, days = 120) => {
  const maxAge = 60 * 60 * 24 * days; // 120 days
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`;
};

// Backend TrackingDto rejects values longer than this, which would fail sign up
const MAX_TRACKING_VALUE_LENGTH = 500;

// Query values are untrusted: they end up in cookies and in rows shown in admin/analytics views
const CLICK_ID_PATTERN = /^[\w\-.~:+%=/]+$/;
// UTM values are free text (spaces and unicode are legitimate), so only markup and control chars are rejected
// eslint-disable-next-line no-control-regex
const FREE_TEXT_PATTERN = /^[^<>\u0000-\u001f\u007f]+$/;
const HOSTNAME_PATTERN = /^[a-z0-9.-]+$/i;
const FREE_TEXT_KEYS: ReadonlySet<string> = new Set([...UTM_KEYS, 'utm_id']);

const isHttpUrl = (url: string): boolean => {
  try {
    return /^https?:$/.test(new URL(url).protocol);
  } catch {
    return false;
  }
};

const isValidTrackingValue = (key: string, value: string): boolean => {
  if (key === 'referrer_url') return isHttpUrl(value);
  if (key === 'referrer_domain') return HOSTNAME_PATTERN.test(value);
  if (FREE_TEXT_KEYS.has(key)) return FREE_TEXT_PATTERN.test(value);
  return CLICK_ID_PATTERN.test(value);
};

// Alternate query param names accepted for a storage key (e.g. LinkedIn's li_fat_id)
const QUERY_PARAM_ALIASES: Record<string, string[]> = {
  liFatId: ['li_fat_id'],
  utm_id: ['utmId'],
  referrer_url: ['referrerUrl'],
  referrer_domain: ['referrerDomain'],
};

// Storage can be blocked or full (e.g. private mode); tracking must never break the app
const readStoredTrackingValue = (key: string): string | null => {
  try {
    return localStorage.getItem(key) || getCookie(key);
  } catch {
    return null;
  }
};

const hasStoredTrackingValue = (key: string): boolean => !!readStoredTrackingValue(key);

// A cut-off identifier is invalid and would mis-attribute, so over-long values are dropped.
// Only the referrer URL is truncated: a cut URL still identifies where the user came from.
const limitTrackingValue = (key: string, value: string): string | null => {
  if (value.length <= MAX_TRACKING_VALUE_LENGTH) return value;
  return key === 'referrer_url' ? value.slice(0, MAX_TRACKING_VALUE_LENGTH) : null;
};

// First-touch: never overwrite a value that is already stored
const storeTrackingValue = (key: string, rawValue: string | null | undefined) => {
  const value = rawValue ? limitTrackingValue(key, rawValue.trim()) : null;
  if (!value || !isValidTrackingValue(key, value) || hasStoredTrackingValue(key)) return;
  try {
    localStorage.setItem(key, value);
  } catch {
    // Fall through: the cookie below still keeps the value when localStorage is unavailable
  }
  setCookie(key, value);
};

const getQueryValue = (params: URLSearchParams, key: string): string | null => {
  for (const name of [key, ...(QUERY_PARAM_ALIASES[key] ?? [])]) {
    const value = params.get(name);
    if (value) return value;
  }
  return null;
};

const getHostname = (url: string): string | null => {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

const OWN_DOMAIN = 'tenantpay.com';

// Navigation from our own website or portal is not a traffic source
const isOwnSite = (url: string): boolean => {
  const hostname = getHostname(url);
  return (
    !!hostname &&
    (hostname === window.location.hostname ||
      hostname === OWN_DOMAIN ||
      hostname.endsWith(`.${OWN_DOMAIN}`))
  );
};

export const useUTMTracker = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const allTrackingKeys = [...UTM_KEYS, ...TRACKING_KEYS, ...REFERRER_KEYS];

    allTrackingKeys.forEach((key) => storeTrackingValue(key, getQueryValue(params, key)));

    // Fall back to the browser referrer when none was passed in the URL
    if (document.referrer && !isOwnSite(document.referrer)) {
      storeTrackingValue('referrer_url', document.referrer);
    }

    const referrerUrl = readStoredTrackingValue('referrer_url');
    if (referrerUrl) {
      storeTrackingValue('referrer_domain', getHostname(referrerUrl));
    }
  }, []);
};
export const getStoredUTMParams = (): StoredTrackingParams => {
  const utm: UTMParams = {};

  const utmMappings: Record<string, keyof UTMParams> = {
    utm_source: 'source',
    utm_medium: 'medium',
    utm_campaign: 'campaign',
    utm_term: 'term',
    utm_content: 'content',
  };

  Object.entries(utmMappings).forEach(([storageKey, utmKey]) => {
    const fromLS = localStorage.getItem(storageKey);
    const fromCookie = getCookie(storageKey);

    const value = fromLS || fromCookie;
    if (value) utm[utmKey] = value;
  });

  const tracking: TrackingParams = {};

  const trackingMappings: Record<string, keyof TrackingParams> = {
    gclid: 'gclid',
    fbclid: 'fbclid',
    msclkid: 'msclkid',
    ttclid: 'ttclid',
    liFatId: 'liFatId',
    twclid: 'twclid',
    gbraid: 'gbraid',
    wbraid: 'wbraid',
    utm_id: 'utmId',
    referrer_url: 'referrerUrl',
    referrer_domain: 'referrerDomain',
  };

  Object.entries(trackingMappings).forEach(([storageKey, trackingKey]) => {
    const fromLS = localStorage.getItem(storageKey);
    const fromCookie = getCookie(storageKey);

    const value = fromLS || fromCookie;
    if (value) tracking[trackingKey] = value;
  });

  const result: StoredTrackingParams = { utm };

  if (Object.keys(tracking).length > 0) {
    result.tracking = tracking;
  }

  return result;
};
export const getRelativeTime = (utcTimestamp: string): string => {
  const localTime = new Date(utcTimestamp);

  const now = new Date();

  const diffMs = now.getTime() - localTime.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);

  if (diffSeconds < 60) return 'just now';
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
};

export function formatToLocalDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export const formatCurrentLongDate = (): string =>
  new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

export const getAvatarInitials = (
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null
): string =>
  `${firstName?.trim().charAt(0) || ''}${lastName?.trim().charAt(0) || ''}`.trim().toUpperCase() ||
  email?.trim().charAt(0).toUpperCase() ||
  'U';

export const getPaginationRange = (
  currentPage: number,
  totalPages: number,
  siblingCount = 1
): (number | string)[] => {
  const totalPageNumbers = siblingCount + 5;

  if (totalPages <= totalPageNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(currentPage - siblingCount, 1);
  const rightSibling = Math.min(currentPage + siblingCount, totalPages);

  const shouldShowLeftDots = leftSibling > 2;
  const shouldShowRightDots = rightSibling < totalPages - 1;

  const firstPage = 1;
  const lastPage = totalPages;

  if (!shouldShowLeftDots && shouldShowRightDots) {
    const range = Array.from({ length: 3 + 2 * siblingCount }, (_, i) => i + 1);
    return [...range, '...', totalPages];
  }

  if (shouldShowLeftDots && !shouldShowRightDots) {
    const range = Array.from(
      { length: 3 + 2 * siblingCount },
      (_, i) => totalPages - (3 + 2 * siblingCount) + i + 1
    );
    return [firstPage, '...', ...range];
  }

  return [
    firstPage,
    '...',
    ...Array.from({ length: rightSibling - leftSibling + 1 }, (_, i) => leftSibling + i),
    '...',
    lastPage,
  ];
};
export const getDayWithSuffix = (day: number | string) => {
  const d = parseInt(day.toString());
  if (d >= 11 && d <= 13) return 'th';
  const lastDigit = d % 10;
  switch (lastDigit) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const formatDayOrdinal = (day: number | string) => {
  const d = parseInt(day.toString(), 10);
  return `${d}${getDayWithSuffix(d)}`;
};
export const getTodayDateFormatted = (): string => {
  const today = new Date();
  const year = today.getUTCFullYear();
  const month = String(today.getUTCMonth() + 1).padStart(2, '0');
  const day = String(today.getUTCDate()).padStart(2, '0');

  return `${year}/${month}/${day}`;
};

/**
 * Capitalizes the first letter of a string and lowercases the rest
 * @param str - The string to capitalize
 * @returns The string with first letter capitalized and rest lowercased, or empty string if input is falsy
 */
export const capitalizeFirstLetter = (str: string | null | undefined): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const isValidEmail = (email: string) =>
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

// utils/dateUtils.ts
export const convertToUTCDateString = (date: string): string => {
  const d = new Date(date);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export function keepAlphanumericAndSpace(str: string): string {
  return str.replace(/[^a-zA-Z0-9 ]/g, '');
}

export function formatUTC(date: string | Date): string {
  const d = new Date(date);

  return (
    d.getUTCFullYear() +
    '-' +
    String(d.getUTCMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getUTCDate()).padStart(2, '0') +
    ' ' +
    String(d.getUTCHours()).padStart(2, '0') +
    ':' +
    String(d.getUTCMinutes()).padStart(2, '0') +
    ':' +
    String(d.getUTCSeconds()).padStart(2, '0')
  );
}

/**
 * Returns the number of whole days remaining until the given date.
 * Returns 0 if the date is in the past or today.
 * @param dateStr - An ISO 8601 date string (e.g., "2026-05-15") or any value
 *   accepted by the `Date` constructor. Invalid strings produce `NaN`-based
 *   arithmetic and will return `0` due to the `Math.max` guard.
 * @returns Non-negative integer count of days until the date.
 */
export function daysUntil(dateStr: string): number {
  const due = new Date(dateStr);
  const now = new Date();
  return Math.max(0, Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
}

/**
 * Formats a date as a short month-and-day string using the en-US locale.
 * @param dateStr - An ISO 8601 date string or any value accepted by the `Date`
 *   constructor. Passing an invalid string will produce "Invalid Date".
 * @returns Abbreviated date string (e.g., "May 15").
 */
export function formatDueDateShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Formats a date with full details (day, month, year) using the en-US locale.
 * @param dateStr - An ISO 8601 date string or any value accepted by the `Date`
 *   constructor. Passing an invalid string will produce "Invalid Date".
 * @returns Full date string (e.g., "2 Sep 2026").
 */
export function formatFullDate(dateStr: string | Date): string {
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Formats a number with 2 decimal places using en-US locale.
 * @param value - The number (or numeric string) to format. Non-numeric strings
 *   are coerced via `Number()` and will produce "NaN.00".
 * @returns Formatted string with 2 decimal places (e.g., "1,234.56").
 */
export function formatNumberWithDecimals(value: number | string): string {
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Formats a value as CAD currency (e.g., "$1,234.56").
 * @param value - The number or numeric string to format
 * @returns Formatted CAD currency string
 */
export function formatCAD(value: number | string): string {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(
    Number(value) || 0
  );
}

/**
 * Formats a value as USD currency with grouped thousands and no trailing .00 for whole numbers.
 * @param value - The number to format
 * @returns Formatted currency string (e.g., "$45,100", "$45,100.25")
 */
export function formatCurrency(value: number | string): string {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return '$0';

  const hasFraction = Math.abs(numericValue % 1) > 0;
  return `$${numericValue.toLocaleString('en-US', {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

export function getCurrentMonth(): string {
  return new Date().toLocaleString('default', { month: 'long' });
}

export function getLastDayOfCurrentMonth(): string {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return lastDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function getCurrentMonthYear(): string {
  return new Date().toLocaleString('default', { month: 'long', year: 'numeric' });
}
